"use client";

import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { DataFilter, type FilterValues } from "@/components/data-filter";
import { DataTable, type ColumnDef } from "@/components/data-table";
import { DataSummary } from "@/components/data-summary";
import { fetchFaDetails, exportFaDetails } from "./actions";
import { FA_COLUMNS } from "./columns";
import type { FaDetailRow } from "@/lib/query";
import { Download } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "3", label: "3" },
  { value: "6", label: "6" },
  { value: "10", label: "10" },
  { value: "13", label: "13" },
  { value: "17", label: "17" },
  { value: "20", label: "20" },
  { value: "24", label: "24" },
  { value: "27", label: "27" },
  { value: "29", label: "29" },
  { value: "31", label: "31" },
];

const TABLE_COLUMNS: ColumnDef<FaDetailRow>[] = FA_COLUMNS.map((col) => ({
  key: col.key,
  header: col.label,
  className: col.key === "vendor_name" || col.key === "material_name" || col.key === "material_spec"
    ? "min-w-[150px]"
    : col.key === "amount" || col.key === "total_amount" || col.key === "tax_amount" || col.key === "unit_price"
      ? "text-right min-w-[100px]"
      : undefined,
  render: col.key === "amount" || col.key === "total_amount" || col.key === "tax_amount" || col.key === "unit_price"
    ? (row: FaDetailRow) => {
        const val = row[col.key as keyof FaDetailRow];
        return val !== null ? `¥${Number(val).toLocaleString("zh-CN", { minimumFractionDigits: 2 })}` : "";
      }
    : undefined,
}));

export default function FaDetailsPage() {
  const [filters, setFilters] = useState<FilterValues>({
    documentNumber: "",
    vendorName: "",
    startDate: "",
    endDate: "",
    status: "",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [data, setData] = useState<FaDetailRow[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ count: 0, totalAmount: 0, taxAmount: 0, grandTotal: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchFaDetails(filters, page, pageSize);
      setData(result.rows);
      setTotal(result.total);
      setSummary(result.summary);
    } catch (err) {
      console.error("查询失败:", err);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  function handleSearch() {
    setPage(1);
    loadData();
  }

  function handlePageSizeChange(size: number) {
    setPageSize(size);
    setPage(1);
  }

  async function handleExport() {
    setExporting(true);
    try {
      const result = await exportFaDetails(filters);
      const blob = new Blob([result.csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("导出失败:", err);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="flex-1 p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">FA 固定资产明细</h1>
        <Button onClick={handleExport} disabled={exporting} size="sm">
          <Download className="h-4 w-4 mr-1" />
          {exporting ? "导出中..." : "导出 CSV"}
        </Button>
      </div>

      <DataFilter
        filters={filters}
        onFiltersChange={setFilters}
        statusOptions={STATUS_OPTIONS}
      />

      <Button onClick={handleSearch} disabled={loading} size="sm" className="w-full md:w-auto">
        {loading ? "查询中..." : "查询"}
      </Button>

      <DataSummary summary={summary} />

      <DataTable
        columns={TABLE_COLUMNS}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
}